// Das Ufer (§6): aufstellen, zuschauen, Bilanz. Zwei Betriebsarten:
// Kampf-Übung (der Kampf-Prototyp aus Phase 3) und Kampf im Run (Phase 4, R27) mit dem Landtrupp.
// Für PC gebaut, ganz ohne Maus spielbar (X10). Liest Sim und Inhalte, schreibt nie hinein;
// den Kampf im Run führt die Run-Simulation über ctx.fight aus.
import { battleEndSound, battleSounds } from './sound.ts';
import { settings } from './settings.ts';
import {
  combatRules,
  defaultKniff,
  getAbility,
  itemLabel,
  getEnemy,
  getSpecies,
  getTrait,
  lineups,
  systemOn,
  viaInfo,
} from '../content/index.ts';
import {
  FrameFlag,
  fragileOf,
  marchReach,
  runBattle,
  stormlingStats,
  type BattleResult,
} from '../sim/index.ts';
import type { Action, InputState } from './input.ts';
import { drawStrip, figuresWanted, loadArt, pixelScale, sprite, toggleFigures, type GlowSpec } from './art.ts';
import { drawArtEffects, figureBox, figureGeo, traitSpots } from './figures.ts';
import { leapPosition, poseAt } from './shore/motion.ts';
import { fxAt, wobbleAt, type ActiveFx } from './shore/signs.ts';
import { tipAt } from './tips.ts';
import { drawBiomeAir, drawBiomeGround, drawBond, drawDuel, drawGate, drawPaw, drawPoisonJumps, drawFelledTrees, duelAt } from './shore/scenery.ts';
import { drawTelegraphs, telegraphsAt } from './practice/draw.ts';
import {
  CLASS_COLORS,
  ENEMY_COLOR,
  drawCursor,
  drawEffects,
  drawGround,
  drawMarch,
  drawRain,
  drawRange,
  drawShieldIcon,
  drawTargetArrow,
  drawUnits,
  drawWardLink,
  setupCanvas,
  STORM_TINT,
  type DrawUnit,
} from './render.ts';
import { summaryHtml } from './summary.ts';
import type { Member, RunShoreContext, Screen, Info } from './shore/state.ts';
import {
  FIELD,
  FZ,
  SPEEDS,
  BASE_SPEED,
  short,
  fmt,
  stormName,
  PRACTICE,
  member,
  statsOf,
  hpOf,
  FIELD_SLOTS,
  S,
  rerollEnemies,
  stormLevel,
  enemyLabels,
  view,
  maxOnField,
  bench,
  retreatIds,
  currentFrame,
  $,
} from './shore/state.ts';
import {
  renderControls,
  renderBench,
  renderSidebar,
  logHtml,
  displayName,
  forgetSidebar,
} from './shore/panels.ts';
export type { Member } from './shore/state.ts';
export type { RunShoreContext } from './shore/state.ts';
export { enemyLabels } from './shore/state.ts';

// Kleinkram und Zustand stehen in shore/state.ts, Kopfleiste und Seitenleiste in shore/panels.ts.

// ---------- Vorschau-Einheiten fürs Aufstellen ----------

function buildSetupUnits(): void {
  const units: DrawUnit[] = [];
  const info: Info[] = [];
  for (const [key, p] of placedWithHeld()) {
    const m = member(key);
    const id = m.species;
    const sp = getSpecies(id);
    const st = statsOf(m);
    if (sp.passive === 'baumhaus') {
      const max = m.profile?.params?.['baumhaus.treeHp'] ?? getAbility('baumhaus').params.treeHp!;
      const hp = m.profile?.treeHp ?? max;
      units.push({ uid: units.length, side: 'friend', kind: 'tree', name: 'Baum', short: '', color: '', radius: getAbility('baumhaus').params.treeRadius!, x: p.x, y: p.y, hp, maxHp: max, mut: 0, flags: hp > 0 ? FrameFlag.alive : 0, traits: [] });
      info.push({ kind: 'tree', id, traits: [], member: m });
    }
    // K6: Den Start-Mut sieht man schon beim Aufstellen
    const mutMax = combatRules.mut.max * (m.profile?.perks?.mutNeed ?? 1);
    // S10: Die Leibwache ist zu Kampfbeginn bereit
    const startMut = isGuardian(m) ? mutMax : Math.min(combatRules.mut.startMax, m.profile?.perks?.startMut ?? sp.classes.find((c) => c.friendClass === m.friendClass)?.startMut ?? 0) * mutMax;
    units.push({
      uid: units.length, side: 'friend', kind: 'friend', name: m.name, short: short(id, sp.name), color: CLASS_COLORS[m.friendClass]!,
      radius: st.radius, x: p.x, y: p.y, hp: hpOf(m), maxHp: st.hp, mut: startMut, mutMax,
      flags: FrameFlag.alive | (sp.passive === 'baumhaus' && (m.profile?.treeHp ?? 1) > 0 ? FrameFlag.onTree : 0), traits: [],
      art: id, friendClass: m.friendClass,
      ...(m.wounded ? { wounded: true } : {}),
      ...(m.look ? { look: m.look } : {}),
    });
    info.push({ kind: 'friend', id, traits: [], member: m });
  }
  const names = enemyLabels(S.enemies);
  for (const [ei, e] of S.enemies.entries()) {
    const traits = e.traits ?? [];
    if (e.infested) {
      // E6: ein befallener Freund auf der Gegnerseite; ein befallenes Eichhörnchen sitzt auf seinem Baum (S5)
      const st = e.infested.profile.stats;
      const onTree = getSpecies(e.infested.species).passive === 'baumhaus';
      if (onTree) {
        const max = e.infested.profile.params?.['baumhaus.treeHp'] ?? getAbility('baumhaus').params.treeHp!;
        units.push({ uid: units.length, side: 'enemy', kind: 'tree', name: 'Baum', short: '', color: '', radius: getAbility('baumhaus').params.treeRadius!, x: e.x, y: e.y, hp: max, maxHp: max, mut: 0, flags: FrameFlag.alive, traits: [] });
        info.push({ kind: 'tree', id: e.infested.species, traits: [] });
      }
      units.push({
        uid: units.length, side: 'enemy', kind: 'infested', name: names[ei]!, short: short(e.infested.species, e.infested.species), color: CLASS_COLORS[e.infested.friendClass]!,
        radius: getSpecies(e.infested.species).radius, x: e.x, y: e.y, hp: st.hp, maxHp: st.hp, mut: 0, flags: FrameFlag.alive | (onTree ? FrameFlag.onTree : 0), traits: traits.map((t) => getTrait(t).name),
        art: e.infested.species, friendClass: e.infested.friendClass, traitIds: traits,
      });
      info.push({ kind: 'enemy', id: e.infested.species, traits, enemyIndex: ei, infested: e.infested });
      continue;
    }
    const def = getEnemy(e.enemy!);
    // G21: dieselbe Rechnung wie im Kampf, mit Würfeln, Eigenschaften und getragenen Items
    const hp = stormlingStats(e, stormLevel(), combatRules).maxHp!;
    const carriedItems = [...(e.item ? [e.item] : []), ...(e.items ?? [])];
    units.push({
      uid: units.length, side: 'enemy', kind: 'stormling', name: names[ei]!, short: short(def.id, def.name), color: ENEMY_COLOR,
      radius: def.stats.radius, x: e.x, y: e.y, hp, maxHp: hp, mut: 0, flags: FrameFlag.alive, traits: [...traits.map((t) => getTrait(t).name), ...carriedItems.map((i) => `trägt ${itemLabel(i)}`)],
      art: def.id, traitIds: traits, ...(names[ei] !== def.name ? { kindName: def.name } : {}),
    });
    info.push({ kind: 'enemy', id: def.id, traits, enemyIndex: ei, items: carriedItems });
  }
  view.setupUnits = units;
  view.setupInfo = info;
  // S10: Wen der Igel mit der Leibwache beschützt, steht in seiner Info
  for (const inf of info) {
    if (inf.kind !== 'friend' || !inf.member || !isGuardian(inf.member)) continue;
    const w = wardKeyOf(inf.member.key);
    if (w) inf.ward = member(w).name;
    // S10: Mit Figuren sitzt der kleine Schild über dem Kopf des Schützlings
    const wi = info.findIndex((i) => i.kind === 'friend' && i.member?.key === w);
    if (wi >= 0) units[wi]!.warded = true;
  }
  // K35 (Fabi, Phase Kern): Zeichen am Freund, je Sturmwesen, das ihn zuerst angreifen würde; K45: von dort, wo es steht
  for (let i = 0; i < units.length; i++) {
    const t = firstTargetOf(i);
    if (t === null) continue;
    const tu = units[t]!;
    tu.attackers = (tu.attackers ?? 0) + 1;
    tu.attackerFrom = [...(tu.attackerFrom ?? []), { x: units[i]!.x, y: units[i]!.y }];
  }
}

/**
 * Wen ein Gegner beim Aufstellen zuerst angreifen würde (Index in view.setupUnits), nach seinem Verhalten (G18).
 * Wer Freunde schützt oder zufällig huscht, hat vorher kein festes erstes Ziel.
 */
function firstTargetOf(idx: number): number | null {
  const e = view.setupUnits[idx];
  const info = view.setupInfo[idx];
  if (!e || !info || info.kind !== 'enemy' || e.kind === 'tree') return null;
  const targets = view.setupUnits.filter((u) => u.side === 'friend' && (u.flags & FrameFlag.onTree) === 0);
  if (!targets.length) return null;
  const behavior = info.infested ? 'nearest' : getEnemy(info.id).behavior;
  if (behavior === 'support' || behavior === 'flitter') return null;
  const gap = (u: DrawUnit) => Math.hypot(u.x - e.x, u.y - e.y) - u.radius - e.radius;
  const nearestOne = targets.reduce((a, b) => (gap(b) < gap(a) ? b : a));
  let first = nearestOne;
  if (behavior === 'hindmost' || behavior === 'raider') first = targets.reduce((a, b) => (b.x < a.x ? b : a));
  if (behavior === 'skirmisher') {
    const radius = getEnemy(info.id).raid?.radius ?? 30;
    const near = targets.filter((u) => Math.hypot(u.x - nearestOne.x, u.y - nearestOne.y) <= radius);
    first = near.reduce((a, b) => (b.hp < a.hp ? b : a), nearestOne);
  }
  return first.uid;
}

// ---------- S10 Leibwache: Schützling beim Aufstellen ----------

/** S10 (Fabi, Phase Kern): Hat dieser Freund die Leibwache als Kniff? */
function isGuardian(m: Member): boolean {
  const entry = getSpecies(m.species).classes.find((c) => c.friendClass === m.friendClass);
  const k = m.profile?.kniff ?? (entry ? defaultKniff(entry) : null);
  return !!k && getAbility(k).mechanic === 'guardian';
}

/** S10: der Schützling, wie ihn der Kampf nimmt: der gewählte, wenn er am Ufer steht, sonst der Zerbrechlichste */
function wardKeyOf(key: string): string | null {
  const others = placedWithHeld().filter(([k]) => k !== key);
  const chosen = S.wards.get(key) ?? member(key).ward ?? null;
  if (chosen && others.some(([k]) => k === chosen)) return chosen;
  return fragileOf(others.map(([k, p]) => ({ key: k, maxHp: statsOf(member(k)).hp, x: p.x })))?.key ?? null;
}

/** S10: der gewählte Schützling für die Simulation; ohne Wahl entscheidet der Kampf selbst */
function wardOf(key: string): { ward?: string } {
  if (!isGuardian(member(key))) return {};
  const w = S.wards.get(key) ?? member(key).ward ?? null;
  return w && w !== key && S.placed.has(w) ? { ward: w } : {};
}

/** S10: Wo der Schild-Griff sitzt: rechts oben am Igel */
function wardKnobOf(key: string): { x: number; y: number } | null {
  const p = S.placed.get(key);
  if (!p || !isGuardian(member(key)) || S.held?.key === key) return null;
  const r = statsOf(member(key)).radius;
  return { x: p.x + r + 2.6, y: p.y - r * 0.7 };
}

function wardKnobAt(at: { x: number; y: number }): string | null {
  for (const key of S.placed.keys()) {
    const k = wardKnobOf(key);
    if (k && Math.hypot(k.x - at.x, k.y - at.y) < 2.6) return key;
  }
  return null;
}

/** S10: den Schützling auf einen Freund setzen */
function setWard(key: string, ward: string): void {
  if (ward === key) return;
  S.wards.set(key, ward);
  say(`${member(key).name} passt jetzt auf ${member(ward).name} auf.`);
  refreshSetup();
}

/** S10 (ohne Maus): Taste T wechselt den Schützling des Igels unter dem Zeiger, sonst des ersten am Ufer */
function cycleWard(): void {
  const here = friendAt(S.cursor);
  const key = here && isGuardian(member(here)) ? here : [...S.placed.keys()].find((k) => isGuardian(member(k)));
  if (!key) return say('Niemand am Ufer hat die Leibwache.');
  const others = [...S.placed.keys()].filter((k) => k !== key);
  if (!others.length) return say(`${member(key).name} hat noch niemanden, auf den er aufpassen kann.`);
  const now = wardKeyOf(key);
  setWard(key, others[(others.indexOf(now ?? '') + 1) % others.length]!);
}

function placedWithHeld(): [string, { x: number; y: number }][] {
  const list = [...S.placed].filter(([key]) => !(S.held && S.held.key === key));
  if (S.held && (S.held.from === 'field' || insideFriendZone(S.held))) list.push([S.held.key, { x: S.held.x, y: S.held.y }]);
  return list;
}

const insideFriendZone = (p: { x: number; y: number }) => p.x >= FZ.xMin && p.x <= FZ.xMax && p.y >= 0 && p.y <= FIELD.height;
const clampToZone = (p: { x: number; y: number }) => ({
  x: Math.min(FZ.xMax, Math.max(FZ.xMin, p.x)),
  y: Math.min(FIELD.height - 4, Math.max(4, p.y)),
});
// ---------- Aufstellen: Aktionen ----------

/** K27: Kann dieser Freund marschieren? Wer auf seinem Baum sitzt, bleibt oben */
function canMarch(m: Member): boolean {
  // K33 (Phase Kern): Marschrichtung ausgeblendet
  if (!systemOn('march')) return false;
  return !(getSpecies(m.species).passive === 'baumhaus' && (m.profile?.treeHp ?? 1) > 0);
}
const reachOf = (m: Member) => marchReach(statsOf(m).moveSpeed);

/** K27: der Marsch als Zielpunkt für die Simulation */
function marchOf(key: string): { march?: { x: number; y: number } } {
  const d = S.marches.get(key);
  const p = S.placed.get(key);
  if (!d || !p || !canMarch(member(key))) return {};
  return { march: { x: Math.min(FIELD.width - 2, Math.max(2, p.x + d.dx)), y: Math.min(FIELD.height - 2, Math.max(2, p.y + d.dy)) } };
}

/** K27: Wo der Griff eines Freundes sitzt; ohne Marsch klein rechts neben ihm */
function knobOf(key: string): { x: number; y: number } | null {
  const p = S.placed.get(key);
  if (!p || !canMarch(member(key))) return null;
  const d = S.marches.get(key);
  return d ? { x: p.x + d.dx, y: p.y + d.dy } : { x: p.x + statsOf(member(key)).radius + 3, y: p.y };
}

function knobAt(at: { x: number; y: number }): string | null {
  for (const key of S.placed.keys()) {
    const k = knobOf(key);
    if (k && Math.hypot(k.x - at.x, k.y - at.y) < 2.6) return key;
  }
  return null;
}

/** K27: Den Pfeil auf den Zeiger ziehen, nicht länger als das Tier laufen kann */
function aimAt(key: string, at: { x: number; y: number }): void {
  const p = S.placed.get(key);
  if (!p) return;
  let dx = at.x - p.x;
  let dy = at.y - p.y;
  const len = Math.hypot(dx, dy);
  const max = reachOf(member(key));
  if (len > max) {
    dx = (dx / len) * max;
    dy = (dy / len) * max;
  }
  S.marches.set(key, { dx, dy });
}

function endAim(): void {
  const key = S.aiming;
  S.aiming = null;
  if (!key) return;
  const d = S.marches.get(key);
  // Zurück auf den Freund gezogen: kein Marsch
  if (d && Math.hypot(d.dx, d.dy) < statsOf(member(key)).radius + 2) S.marches.delete(key);
  renderSidebar();
}

function say(msg: string): void {
  S.message = msg;
  renderSidebar();
}

function pickUp(key: string, from: 'field' | 'bench', at: { x: number; y: number }, byMouse: boolean): void {
  S.held = { key, from, x: at.x, y: at.y, byMouse };
  S.cursor.holding = key;
}

function drop(at: { x: number; y: number }, toBench: boolean): void {
  const h = S.held;
  if (!h) return;
  S.held = null;
  S.cursor.holding = null;
  if (toBench) {
    S.placed.delete(h.key);
    S.marches.delete(h.key);
  } else if (insideFriendZone(at) || h.from === 'field') {
    if (h.from === 'bench' && S.placed.size >= maxOnField()) {
      say(`Alle ${S.fieldSlots} Plätze am Ufer sind belegt. Erst jemanden an Bord ziehen.`);
    } else {
      S.placed.set(h.key, clampToZone(at));
    }
  }
  refreshSetup();
}

function friendAt(p: { x: number; y: number }): string | null {
  // Mit Figuren zählt der Körper, nicht nur der Kreis am Boden
  const scale = canvas.width / FIELD.width;
  for (const [i, u] of view.setupUnits.entries()) {
    const info = view.setupInfo[i];
    if (info?.kind !== 'friend' || !info.member || !S.placed.has(info.member.key) || S.held?.key === info.member.key) continue;
    const box = figureBox(u, scale);
    if (box && p.x >= box.x0 && p.x <= box.x1 && p.y >= box.y0 && p.y <= box.y1) return info.member.key;
  }
  let best: string | null = null;
  let bestD = Infinity;
  for (const [id, q] of S.placed) {
    const r = statsOf(member(id)).radius + 2;
    const d = Math.hypot(q.x - p.x, q.y - p.y);
    if (d < r && d < bestD) {
      best = id;
      bestD = d;
    }
  }
  return best;
}

function unitAt(units: DrawUnit[], p: { x: number; y: number }): number | null {
  let best: number | null = null;
  let bestD = Infinity;
  const scale = canvas.width / FIELD.width;
  for (const u of units) {
    if ((u.flags & FrameFlag.alive) === 0 && !((ghostsVisible() || (u.flags & FrameFlag.pushedOut) !== 0) && u.goneLabel)) continue;
    // Mit Figuren zählt der Körper; wer vorn steht, liegt beim Zeigen oben
    const box = (u.flags & FrameFlag.alive) !== 0 ? figureBox(u, scale) : null;
    if (box && p.x >= box.x0 && p.x <= box.x1 && p.y >= box.y0 && p.y <= box.y1) {
      const d = Math.hypot(box.cx - p.x, box.cy - p.y) * 0.5 - u.y * 0.01;
      if (d < bestD) {
        best = u.uid;
        bestD = d;
      }
      continue;
    }
    const d = Math.hypot(u.x - p.x, u.y - p.y);
    // Eichhörnchen vor seinem Baum treffen
    const bias = u.kind === 'tree' ? 1.5 : 0;
    if (d < u.radius + 2 && d + bias < bestD) {
      best = u.uid;
      bestD = d + bias;
    }
  }
  return best;
}

function setLineup(i: number): void {
  S.lineupIdx = (i + lineups.length) % lineups.length;
  S.attempt = 0;
  rerollEnemies();
  refreshSetup();
}

function setStorm(delta: number): void {
  const ids = combatRules.stormLevels.map((l) => l.id);
  const i = Math.min(ids.length - 1, Math.max(0, ids.indexOf(S.storm) + delta));
  S.storm = ids[i]!;
  refreshSetup();
}

/** Fabi, Phase 3: alle zurück aufs Boot, um neu aufzustellen */
function allAboard(): void {
  S.placed.clear();
  S.marches.clear();
  S.held = null;
  S.cursor.holding = null;
  refreshSetup();
}

function cycleRetreat(): void {
  if (S.run?.noRetreat) return;
  const ids = retreatIds();
  S.retreat = ids[(ids.indexOf(S.retreat) + 1) % ids.length]!;
  refreshSetup();
}

function newAttempt(): void {
  S.attempt += 1;
  rerollEnemies();
  S.message = 'Neue Eigenschaften gewürfelt.';
  refreshSetup();
}

function startBattle(): void {
  if (S.placed.size === 0) return say('Stell mindestens einen Freund aufs Ufer.');
  if (S.mode === 'run' && S.run) {
    // R27: Der Run führt den Kampf aus und übernimmt das Ergebnis
    S.result = S.run.fight([...S.placed].map(([key, p]) => ({ id: key, x: p.x, y: p.y, ...marchOf(key), ...wardOf(key) })), S.retreat);
  } else {
    const keys = [...S.placed.keys()];
    const setup = {
      friends: [...S.placed].map(([key, p]) => {
        const w = wardOf(key).ward;
        return { id: key, species: member(key).species, friendClass: member(key).friendClass, name: member(key).name, x: p.x, y: p.y, ...marchOf(key), ...(w ? { ward: keys.indexOf(w) } : {}) };
      }),
      enemies: S.enemies,
      stormLevel: S.storm,
      fieldSlots: S.fieldSlots,
      retreat: S.retreat,
    };
    const seed = (Math.random() * 2 ** 31) | 0;
    S.result = runBattle(setup, seed, { recordFrames: true });
  }
  beginPlayback(S.result);
}

/** Den ausgerechneten Kampf von vorn abspielen */
function beginPlayback(result: BattleResult): void {
  S.result = result;
  S.crits = new Map();
  for (const e of result.events) if (e.type === 'damage' && e.crit && e.from !== null) S.crits.set(e.from, (S.crits.get(e.from) ?? 0) + 1);
  S.playT = 0;
  S.paused = false;
  // Spieltest Phase 4 (Fabi): Jeder Kampf beginnt wieder mit dem gleichen Tempo, seit dem Menü dem aus den Optionen
  S.speed = settings().speed;
  renderControls();
  $('pause').textContent = 'Pause';
  S.endOverlay = false;
  S.reviewing = false;
  S.hoverUid = null;
  S.selectedUid = null;
  S.message = '';
  S.maxSeenT = 0;
  setBattleDone(false);
  show('battle');
}

// ---------- Bildschirme ----------

function show(screen: Screen): void {
  S.screen = screen;
  document.body.dataset.screen = screen;
  if (screen !== 'battle') setEndOverlay(false);
  if (screen === 'summary' && S.result) {
    const run = S.mode === 'run' ? S.run : null;
    $('summary').innerHTML = summaryHtml(S.result, {
      lineup: run ? run.title : lineups[S.lineupIdx]!.name,
      storm: S.storm,
      crits: S.crits,
      againLabel: run ? 'Weiter' : 'Neuer Versuch',
      extra: run ? run.afterNotes() : '',
      fine: run ? 'Klickt auf eine Zeile für den Verlauf.' : undefined,
      kept: run?.kept?.() ?? [],
      ...(run?.xpOf ? { xpOf: run.xpOf } : {}),
    });
    $('again').addEventListener('click', () => leaveSummary());
    $('replay').addEventListener('click', () => replay());
    // L7: Verlauf einer Figur auch nach dem Kampf, per Klick auf ihre Zeile
    S.playT = S.result.duration;
    for (const row of document.querySelectorAll<HTMLElement>('#summary tr[data-uid]')) {
      row.addEventListener('click', () => showSummaryLog(Number(row.dataset.uid)));
    }
    ($('again') as HTMLButtonElement).focus({ preventScroll: true });
    run?.summaryShown?.($('summary'));
  }
  if (screen === 'setup') refreshSetup();
  renderSidebar();
  renderControls();
}

/** Aus der Übersicht weiter: in der Übung ein neuer Versuch, im Run zurück aufs Boot */
function leaveSummary(): void {
  if (S.mode === 'run' && S.run) {
    const done = S.run.onDone;
    S.run = null;
    done();
    return;
  }
  newAttempt();
  show('setup');
}

function replay(): void {
  S.playT = 0;
  S.paused = false;
  $('pause').textContent = 'Pause';
  S.endOverlay = false;
  S.reviewing = false;
  show('battle');
}

function refreshSetup(): void {
  if (S.setupSelected !== null && S.setupSelected >= S.enemies.length) S.setupSelected = null;
  buildSetupUnits();
  renderControls();
  renderBench();
  renderSidebar();
}

// ---------- Abspielen ----------

/** Umrisse der Gegangenen nur beim Betrachten, in der Pause oder am Ende */
function ghostsVisible(): boolean {
  return S.screen === 'battle' && !!S.result && (S.reviewing || S.paused || S.playT >= S.result.duration);
}

/** Wann und wie jede Figur das Ufer verlassen hat */
let goneCache: { result: BattleResult | null; map: Map<number, { t: number; label: string }> } = { result: null, map: new Map() };

/** T12: Treffer pro Figur, einmal pro Kampf gesammelt */
let hitCache: { result: BattleResult | null; map: Map<number, { t: number; share: number }[]> } = { result: null, map: new Map() };
function hitsOf(uid: number): { t: number; share: number }[] {
  const r = S.result!;
  if (hitCache.result !== r) {
    const map = new Map<number, { t: number; share: number }[]>();
    for (const e of r.events) {
      if (e.type !== 'damage' || e.amount <= 0) continue;
      const u = r.units[e.to];
      if (!u) continue;
      const list = map.get(e.to) ?? [];
      list.push({ t: e.t, share: e.amount / u.maxHp });
      map.set(e.to, list);
    }
    hitCache = { result: r, map };
  }
  return hitCache.map.get(uid) ?? [];
}

/** T12 (Fabi, Spieltest Phase 4): Geister-Leben (das Leben von eben) und wie heftig der letzte Treffer war */
function impactAt(uid: number, i0: number): { ghostHp: number; hit: number } {
  const r = S.result!;
  const window = Math.round(0.6 / combatRules.tickSeconds);
  let ghost = 0;
  for (let i = Math.max(0, i0 - window); i <= i0; i++) ghost = Math.max(ghost, r.frames[i]!.units[uid]![2]);
  let hit = 0;
  for (const h of hitsOf(uid)) {
    const age = S.playT - h.t;
    if (age < 0) break;
    if (age <= 0.18) hit = Math.max(hit, Math.min(1, 0.35 + h.share * 4) * (1 - age / 0.18));
  }
  return { ghostHp: ghost, hit };
}
function goneInfo(): Map<number, { t: number; label: string }> {
  const r = S.result!;
  if (goneCache.result === r) return goneCache.map;
  const map = new Map<number, { t: number; label: string }>();
  for (const e of r.events) {
    if (e.type === 'down') {
      const u = r.units[e.unit]!;
      // F42 (Phase Kern): Wer zum ersten Mal fällt, kommt verwundet zurück
      map.set(e.unit, { t: e.t, label: u.kind === 'tree' ? 'gefällt' : u.side === 'friend' ? (u.wounded ? 'Abschied' : 'verwundet') : 'vertrieben' });
    } else if (e.type === 'escape') {
      map.set(e.unit, { t: e.t, label: r.units[e.unit]!.side === 'friend' ? 'in Sicherheit' : 'entkommen' });
    } else if (e.type === 'pushedOut') {
      map.set(e.unit, { t: e.t, label: 'wartet am Rand' });
    }
  }
  goneCache = { result: r, map };
  return map;
}

/** T9 (Fabi): Flug-Kniffe wie der Sturzflug: blitzschnell hin, Treffer, zurück. Reine Darstellung, kostet keine Kampfzeit. */
const FLIGHT_OUT = 0.15;
const FLIGHT_BACK = 0.2;
let flightCache: { result: BattleResult | null; flights: { uid: number; target: number; t: number }[] } = { result: null, flights: [] };
function flights(): { uid: number; target: number; t: number }[] {
  const r = S.result!;
  if (flightCache.result !== r) {
    const list: { uid: number; target: number; t: number }[] = [];
    for (const e of r.events) if (e.type === 'kniff' && e.target !== null && getAbility(e.kniff).visual === 'flight') list.push({ uid: e.unit, target: e.target, t: e.t });
    flightCache = { result: r, flights: list };
  }
  return flightCache.flights;
}

function applyFlights(units: DrawUnit[]): void {
  for (const f of flights()) {
    const dt = S.playT - f.t;
    if (dt < -FLIGHT_OUT || dt > FLIGHT_BACK) continue;
    const bird = units[f.uid];
    const prey = units[f.target];
    if (!bird || !prey) continue;
    const dx = prey.x - bird.x;
    const dy = prey.y - bird.y;
    const d = Math.hypot(dx, dy) || 1;
    // Bis an den Rand des Ziels, nicht hinein
    const reach = Math.max(0, d - prey.radius - bird.radius);
    const k = dt < 0 ? 1 + dt / FLIGHT_OUT : 1 - dt / FLIGHT_BACK;
    bird.x += (dx / d) * reach * k;
    bird.y += (dy / d) * reach * k;
  }
}

/** Zeit für Bewegungen und Bilder: nach dem Ende des Kampfs läuft sie weiter, damit Vertriebene verpuffen und Freunde stehen */
const visT = (): number => (S.result && S.playT >= S.result.duration - 1e-6 ? S.result.duration + S.afterT : S.playT);

function battleUnits(): DrawUnit[] {
  const r = S.result!;
  if (r.frames.length === 0) return [];
  if (!Number.isFinite(S.playT)) S.playT = 0;
  // Am Ende immer das letzte Bild, sonst zeigt ein Rundungsfehler den vorletzten Takt (Gegner noch da)
  const k = S.playT >= r.duration - 1e-6 ? r.frames.length - 1 : Math.max(0, S.playT / combatRules.tickSeconds + 1e-6);
  const i0 = Math.min(r.frames.length - 1, Math.floor(k));
  const i1 = Math.min(r.frames.length - 1, i0 + 1);
  const a = k - i0;
  const f0 = r.frames[i0]!;
  const f1 = r.frames[i1]!;
  const figures = figuresWanted();
  // S10: Wer gerade von einer Leibwache beschützt wird
  const warded = new Set(r.units.filter((g) => g.wardUid !== null && (f0.units[g.uid]![4] & FrameFlag.alive) !== 0).map((g) => g.wardUid!));
  // S13 Pfote halten (Runde 7): wer gerade die Pfote hält oder gehalten wird (beide stehen nah genug beieinander)
  const pawHeld = new Set<number>();
  const alive0 = (uid: number) => ((f0.units[uid]?.[4] ?? 0) & FrameFlag.alive) !== 0;
  for (const u of r.units) {
    if (!u.paw || !alive0(u.uid)) continue;
    for (const o of u.paw.with) {
      const a = f0.units[u.uid]!;
      const b = f0.units[o];
      const v = r.units[o];
      if (!b || !v || !alive0(o) || Math.hypot(a[0] - b[0], a[1] - b[1]) - u.radius - v.radius > u.paw.reach) continue;
      pawHeld.add(u.uid);
      pawHeld.add(o);
    }
  }
  // Version 3 des Manifests: Bilder für Kniffe und Eigenschaften an den Figuren
  const fx = figures ? fxAt(r, visT()) : null;
  const list: DrawUnit[] = r.units.map((u) => {
    const unitFx = fx?.get(u.uid);
    const p0 = f0.units[u.uid]!;
    const p1 = f1.units[u.uid]!;
    // Sprünge nicht weich ziehen; mit Figuren springen sie sichtbar (K39)
    const jump = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]) > 6;
    const leap = figures ? leapPosition(r, u.uid, visT()) : null;
    return {
      uid: u.uid,
      side: u.side,
      kind: u.kind,
      name: u.side === 'enemy' ? displayName(u.uid) : u.name,
      short: u.kind === 'tree' ? '' : short(u.defId, u.name),
      color: u.kind === 'stormling' ? ENEMY_COLOR : CLASS_COLORS[u.friendClass ?? ''] ?? '#ccc',
      radius: u.radius,
      x: leap ? leap.x : jump ? p0[0] : p0[0] + (p1[0] - p0[0]) * a,
      y: leap ? leap.y : jump ? p0[1] : p0[1] + (p1[1] - p0[1]) * a,
      hp: p0[2],
      maxHp: u.maxHp,
      mut: p0[3],
      mutMax: u.mutMax,
      poison: p0[5] ?? 0,
      shield: p0[7] ?? 0,
      flags: p0[4],
      traits: u.traits.map((t) => getTrait(t).name),
      goneLabel: (() => {
        const g = goneInfo().get(u.uid);
        return g && g.t <= S.playT ? g.label : undefined;
      })(),
      statusWords: statusWordsAt(u.uid),
      ...impactAt(u.uid, i0),
      ...(u.wounded ? { wounded: true } : {}),
      ...(u.kind === 'tree' ? {} : { art: u.defId, traitIds: u.traits }),
      ...(u.kind === 'friend' && u.runId ? { look: S.roster.find((m) => m.key === u.runId)?.look ?? 0 } : {}),
      ...(u.kind === 'stormling' ? { kindName: getEnemy(u.defId).name } : {}),
      ...(u.friendClass ? { friendClass: u.friendClass } : {}),
      ...(figures && u.kind !== 'tree' ? { pose: poseAt(r, u.uid, visT(), sheetOf(u.kind, u.defId)) } : {}),
      ...(warded.has(u.uid) ? { warded: true } : {}),
      ...(pawHeld.has(u.uid) ? { pawHeld: true } : {}),
      ...(unitFx ? { fx: unitFx } : {}),
      ...(figures ? glowOf(unitFx, p0[4]) : {}),
      ...(figures && wobbleAt(r, u.uid, visT()) ? { wobble: wobbleAt(r, u.uid, visT()) } : {}),
    };
  });
  // K35 (Fabi, Phase Kern): Zeichen je Sturmwesen, das ihn gerade angreift; K45: als Spitze am Ring, von dort, wo es steht
  for (const u of r.units) {
    const d = f0.units[u.uid]!;
    const target = d[6];
    if (u.side !== 'enemy' || (d[4] & FrameFlag.alive) === 0 || target < 0) continue;
    const tu = list[target];
    if (!tu || tu.side !== 'friend' || tu.kind === 'tree') continue;
    tu.attackers = (tu.attackers ?? 0) + 1;
    tu.attackerFrom = [...(tu.attackerFrom ?? []), { x: list[u.uid]!.x, y: list[u.uid]!.y }];
  }
  // T9: Flug-Kniffe wie der Sturzflug; mit Figuren fliegt der Vogel als Sprung (K39)
  if (!figures) applyFlights(list);
  return list;
}

/** Vorschlag des Grafik-Leads für jede andere Stärkung (Nussgeschenk, Gezeter): leiser als der Heißhunger */
const EMPOWERED_GLOW: GlowSpec = { color: '#ffd870', tint: 0.08, alpha: [0.35, 0.75], pulseMs: 1100, width: 1 };

/**
 * K38, Feld glow des Grafik-Leads (Version 4): der Schein um die Figur. Heißhunger und Hautgift tragen ihn im Manifest
 * (fehlt er dort, gilt der leise Schein der Stärkung), jede andere Stärkung bekommt den leisen.
 */
function glowOf(fx: ActiveFx[] | undefined, flags: number): { glow?: GlowSpec } {
  for (const id of ['heisshunger', 'hautgift']) {
    const f = fx?.find((x) => x.id === id);
    if (f) return { glow: sprite(f.kind, f.id)?.glow ?? EMPOWERED_GLOW };
  }
  if (flags & FrameFlag.empowered) return { glow: EMPOWERED_GLOW };
  return {};
}

/** Die Bewegungsbilder einer Figur aus dem Manifest */
function sheetOf(kind: string, defId: string) {
  const sp = kind === 'stormling' ? sprite('enemy', defId) : sprite('species', defId);
  return (kind === 'infested' ? sp?.infested : sp?.sheet) ?? undefined;
}

/** Fabi (Phase Kern): Welche Fähigkeit oder welches Item einen Zustand ausgelöst hat, steht über der Figur, nicht ein fester Name */
const LABELLED: [string, number][] = [
  ['guarded', FrameFlag.guarded],
  ['amber', FrameFlag.shielded],
  ['empowered', FrameFlag.empowered],
];
let statusCache: { result: BattleResult | null; byUnit: Map<number, { t: number; status: string; via?: string }[]> } = { result: null, byUnit: new Map() };
function statusWordsAt(uid: number): Partial<Record<number, string>> | undefined {
  const r = S.result!;
  if (statusCache.result !== r) {
    const byUnit = new Map<number, { t: number; status: string; via?: string }[]>();
    for (const e of r.events) if (e.type === 'status' && LABELLED.some(([st]) => st === e.status)) (byUnit.get(e.unit) ?? byUnit.set(e.unit, []).get(e.unit)!).push(e);
    statusCache = { result: r, byUnit };
  }
  const list = statusCache.byUnit.get(uid);
  if (!list) return undefined;
  const out: Partial<Record<number, string>> = {};
  for (const [status, flag] of LABELLED) {
    let last: { via?: string } | undefined;
    for (const e of list) if (e.status === status && e.t <= S.playT + 1e-6) last = e;
    if (last?.via) out[flag] = viaInfo(last.via.split('|')[0]!).short;
  }
  return out;
}

function rainAt(t: number): number {
  let m = 1;
  for (const e of S.result!.events) {
    if (e.t > t) break;
    if (e.type === 'heavyRain') m = e.multiplier;
  }
  return m;
}

// ---------- Zeichnen pro Bild ----------

const canvas = $<HTMLCanvasElement>('field');

function pointerToField(ev: { clientX: number; clientY: number }): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return { x: ((ev.clientX - rect.left) / rect.width) * FIELD.width, y: ((ev.clientY - rect.top) / rect.height) * FIELD.height };
}

/** Reichweite des Freundes, den man gerade setzt, oder des Kreises unter dem Zeiger */
function drawSetupRange(ctx: CanvasRenderingContext2D, scale: number): void {
  if (S.held) {
    const m = member(S.held.key);
    const st = statsOf(m);
    drawRange(ctx, scale, S.held.x, S.held.y, st.radius, st.range, CLASS_COLORS[m.friendClass]!);
    return;
  }
  if (S.hoverUid === null) return;
  const u = view.setupUnits[S.hoverUid];
  const info = view.setupInfo[S.hoverUid];
  if (!u || !info || info.kind === 'tree') return;
  const st = info.member ? statsOf(info.member) : info.infested ? info.infested.profile.stats : getEnemy(info.id).stats;
  drawRange(ctx, scale, u.x, u.y, u.radius, st.range, info.kind === 'friend' ? u.color : '#b9b7e0');
}

/** Wen der angeklickte Gegner zuerst angreifen würde (G18-Verhalten) */
function drawFirstTarget(ctx: CanvasRenderingContext2D, scale: number): void {
  // Spieltest Phase 4 (Fabi): wie bei Freunden schon beim Draufzeigen, nicht erst nach dem Anklicken
  const hovered = S.hoverUid !== null && view.setupInfo[S.hoverUid]?.kind === 'enemy' ? S.hoverUid : null;
  if (hovered === null && S.setupSelected === null) return;
  const idx = hovered ?? view.setupInfo.findIndex((i) => i.enemyIndex === S.setupSelected);
  const e = view.setupUnits[idx];
  const t = firstTargetOf(idx);
  if (!e || t === null) return;
  drawTargetArrow(ctx, scale, e, view.setupUnits[t]!);
}

/** K35 (Fabi, Phase Kern): Fährt man über einen Freund, zeigen Pfeile, wer ihn zuerst angreifen würde */
function drawSetupAttackers(ctx: CanvasRenderingContext2D, scale: number): void {
  if (S.hoverUid === null || view.setupUnits[S.hoverUid]?.side !== 'friend') return;
  for (let i = 0; i < view.setupUnits.length; i++) if (firstTargetOf(i) === S.hoverUid) drawTargetArrow(ctx, scale, view.setupUnits[i]!, view.setupUnits[S.hoverUid]!, '#ff7a6a');
}

/** S10: Linie zum Schützling und der Schild-Griff an jedem Igel mit Leibwache */
function drawWards(ctx: CanvasRenderingContext2D, scale: number): void {
  for (const key of S.placed.keys()) {
    if (!isGuardian(member(key))) continue;
    const gi = view.setupInfo.findIndex((i) => i.kind === 'friend' && i.member?.key === key);
    const g = view.setupUnits[gi];
    if (!g) continue;
    if (S.wardAiming === key && S.wardAim) {
      drawWardLink(ctx, scale, g, { x: S.wardAim.x, y: S.wardAim.y, radius: 1 }, true);
      // Version 4: beim Ziehen folgt der Griff der Maus (Bild gezogen)
      drawStrip(ctx, 'marker', 'leibwache-griff', 2, S.wardAim.x * scale, S.wardAim.y * scale, pixelScale(scale));
    } else {
      const w = wardKeyOf(key);
      const wu = w ? view.setupUnits[view.setupInfo.findIndex((i) => i.kind === 'friend' && i.member?.key === w)] : undefined;
      // Mit Figuren zeichnet die Figur den Schild selbst über dem Kopf
      if (wu) drawWardLink(ctx, scale, g, wu, S.hoverUid === gi, !figureGeo(wu, scale));
    }
    const k = wardKnobOf(key);
    if (!k) continue;
    // S10 (Fabi, 17.10.2026 nachts: „wie wählt man die Leibwache?“): Der Schild am Igel pulsiert sacht, damit man sieht,
    // dass man ihn greifen und auf einen Freund ziehen kann; beim Darüberfahren steht dabei, wozu
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 380);
    ctx.save();
    ctx.strokeStyle = '#f6c76b';
    ctx.globalAlpha = 0.25 + 0.35 * pulse;
    ctx.lineWidth = Math.max(1.5, scale * 0.35);
    ctx.beginPath();
    ctx.arc(k.x * scale, k.y * scale, scale * (2.1 + pulse * 0.5), 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    const hover = wardKnobAt(S.cursor) === key && !S.held && S.wardAiming === null;
    // Version 4: der Griff des Grafik-Leads (ruhig, Maus darauf); sonst der gezeichnete Schild
    if (S.wardAiming !== key && !drawStrip(ctx, 'marker', 'leibwache-griff', hover ? 1 : 0, k.x * scale, k.y * scale, pixelScale(scale))) drawShieldIcon(ctx, scale, k.x, k.y, '#f6c76b');
    if (hover) {
      ctx.save();
      ctx.font = `${Math.max(10, scale * 1.9)}px system-ui, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#f6c76b';
      ctx.fillText('auf einen Freund ziehen', (k.x + 2.6) * scale, k.y * scale);
      ctx.restore();
    }
  }
}

/**
 * Spieltest Phase 4 (Fabi): Wen ein eigener Freund zuerst angreift, beim Draufzeigen und beim Verschieben,
 * also immer passend zur Position. Wie im Kampf: der nächste Gegner; ein Eichhörnchen auf dem Baum springt mit
 * seinem Kniff auf den Gegner mit dem wenigsten Leben; wer heilt, hat kein Ziel unter den Gegnern.
 */
function drawFriendTarget(ctx: CanvasRenderingContext2D, scale: number): void {
  const idx = S.held ? view.setupInfo.findIndex((i) => i.kind === 'friend' && i.member?.key === S.held!.key) : S.hoverUid;
  if (idx === null || idx < 0) return;
  const f = view.setupUnits[idx];
  const info = view.setupInfo[idx];
  if (!f || !info || info.kind !== 'friend' || !info.member) return;
  if ((info.member.profile?.attackHeal ?? getSpecies(info.member.species).classes.find((c) => c.friendClass === info.member!.friendClass)?.attackHeal ?? 0) > 0) return;
  const foes = view.setupUnits.filter((u) => u.side === 'enemy' && u.kind !== 'tree');
  if (!foes.length) return;
  const gap = (u: DrawUnit) => Math.hypot(u.x - f.x, u.y - f.y) - u.radius - f.radius;
  const weakest = (a: DrawUnit, b: DrawUnit) => (b.hp < a.hp - 1e-6 || (Math.abs(b.hp - a.hp) <= 1e-6 && gap(b) < gap(a)) ? b : a);
  const first = (f.flags & FrameFlag.onTree) !== 0 ? foes.reduce(weakest) : foes.reduce((a, b) => (gap(b) < gap(a) ? b : a));
  drawTargetArrow(ctx, scale, f, first, '#f6c76b');
}

function draw(): void {
  const { ctx, scale } = setupCanvas(canvas);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  // R41, R42 (Erste Fassung): Boden je Biom, am Durchlass der Durchlass am rechten Rand
  const biome = S.mode === 'run' ? S.run?.biome : undefined;
  // Runde 7 (Hinweis des Grafik-Leads): Der Durchlass steht mit dem Boden zusammen unter der Abdunklung des Unwetters
  const groundRain = S.result && S.screen !== 'setup' ? rainAt(S.playT) : 1;
  const base = biome
    ? () => {
        const drawn = drawBiomeGround(ctx, scale, biome, groundRain);
        if (S.run?.boss) drawGate(ctx, scale, biome, performance.now() / 1000, groundRain);
        return drawn;
      }
    : undefined;
  const now = performance.now() / 1000;
  if (S.screen === 'setup') {
    drawGround(ctx, scale, true, 1, S.storm, base);
    drawSetupRange(ctx, scale);
    drawUnits(ctx, scale, view.setupUnits, { highlight: S.hoverUid ?? (S.setupSelected !== null ? view.setupInfo.findIndex((i) => i.enemyIndex === S.setupSelected) : null), showTraits: true, t: performance.now() / 1000 });
    drawFirstTarget(ctx, scale);
    drawFriendTarget(ctx, scale);
    // B25: Verbundene aus dem Gemeinschaftsraum
    const bonds = S.mode === 'run' ? S.run?.bonds : undefined;
    if (bonds) {
      const at = (key: string) => view.setupUnits[view.setupInfo.findIndex((i) => i.kind === 'friend' && i.member?.key === key)];
      for (const g of bonds.groups)
        for (let i = 0; i < g.length; i++)
          for (let j = i + 1; j < g.length; j++) {
            const a = at(g[i]!);
            const b = at(g[j]!);
            if (a && b) drawBond(ctx, scale, a, b, bonds.reach, true, now);
          }
    }
    drawSetupAttackers(ctx, scale);
    drawWards(ctx, scale);
    // K27: Marschrichtungen; Gegner, die loslaufen, zeigen es vorher (P4)
    for (const [i, e] of S.enemies.entries()) {
      if (!e.march || !systemOn('march')) continue;
      const u = view.setupUnits[view.setupInfo.findIndex((x) => x.enemyIndex === i)];
      if (u) drawMarch(ctx, scale, u.x, u.y, u.radius, e.march, { color: '#b9b7e0', knob: false });
    }
    for (const key of S.placed.keys()) {
      if (S.held?.key === key) continue;
      const m = member(key);
      if (!canMarch(m)) continue;
      const p = S.placed.get(key)!;
      const d = S.marches.get(key);
      drawMarch(ctx, scale, p.x, p.y, statsOf(m).radius, d ? { x: p.x + d.dx, y: p.y + d.dy } : null, { color: CLASS_COLORS[m.friendClass]!, knob: true, ...(S.aiming === key ? { reach: reachOf(m) } : {}) });
    }
    if (biome) drawBiomeAir(ctx, scale, biome, now, !!S.run?.boss, 1, STORM_TINT[S.storm] ?? 0);
    const label = S.held && !S.held.byMouse ? member(S.held.key).name : null;
    drawCursor(ctx, scale, S.cursor, label);
    if (S.held && S.held.byMouse && !insideFriendZone(S.held) && S.held.from === 'bench') {
      // Beim Ziehen von der Bank: Geist an der Maus, auch außerhalb der Hälfte
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = CLASS_COLORS[member(S.held.key).friendClass]!;
      ctx.beginPath();
      ctx.arc(S.held.x * scale, S.held.y * scale, 4 * scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  } else if (S.result) {
    const units = battleUnits();
    drawGround(ctx, scale, false, rainAt(S.playT), S.storm, base);
    // K53 (Erste Fassung, Fabi): Große Angriffe der Sturmwesen sind am Boden angesagt, wie im Übungsufer
    const telegraphs = telegraphsAt(S.result, S.playT);
    drawTelegraphs(ctx, scale, telegraphs, units, S.playT, true, 'ground');
    // B25: Band zwischen Verbundenen, solange beide stehen und nah beieinander sind
    for (const u of S.result.units) {
      if (!u.bond) continue;
      const a = units[u.uid];
      for (const o of u.bond.with) {
        const b = units[o];
        if (!a || !b || o < u.uid || (a.flags & FrameFlag.alive) === 0 || (b.flags & FrameFlag.alive) === 0) continue;
        drawBond(ctx, scale, a, b, u.bond.reach, false, visT());
      }
    }
    // S13 Pfote halten: dasselbe Zeichen zwischen Otter und Freund, solange sie nah beieinander sind
    for (const u of S.result.units) {
      if (!u.paw) continue;
      const a = units[u.uid];
      for (const o of u.paw.with) {
        const b = units[o];
        if (!a || !b || o < u.uid || (a.flags & FrameFlag.alive) === 0 || (b.flags & FrameFlag.alive) === 0) continue;
        if (S.result.units[o]?.bond?.with.includes(u.uid)) continue;
        if (!drawPaw(ctx, scale, a, b, u.paw.reach, visT())) drawBond(ctx, scale, a, b, u.paw.reach, false, visT());
      }
    }
    // S13 Baum fällen: Der Baum steht hinter dem nagenden Biber, bis er fällt
    drawFelledTrees(ctx, scale, S.result, S.playT, 'stand');
    drawUnits(ctx, scale, units, { highlight: S.hoverUid ?? S.selectedUid, showTraits: true, ghosts: ghostsVisible(), t: visT() });
    // T31, K39: Geschosse, Funken, Heilung, Staub (nur mit Figuren)
    drawArtEffects(ctx, scale, units, S.result, visT());
    const byUid = new Map(units.map((u) => [u.uid, u]));
    // Spieltest Phase 4 (Fabi): Auch im Kampf zeigt ein Pfeil, wen die angeklickte oder gezeigte Figur gerade angreift
    for (const uid of new Set([S.hoverUid, S.selectedUid])) {
      if (uid === null) continue;
      const target = currentFrame()?.units[uid]?.[6] ?? -1;
      const from = byUid.get(uid);
      const to = target >= 0 ? byUid.get(target) : undefined;
      if (from && to && (from.flags & FrameFlag.alive) !== 0 && (to.flags & FrameFlag.alive) !== 0) drawTargetArrow(ctx, scale, from, to, from.side === 'friend' ? (to.side === 'friend' ? '#9fd08a' : '#f6c76b') : undefined);
      // K35 (Fabi, Phase Kern): Über einem Freund zeigen Pfeile, wer ihn gerade angreift
      if (from && from.side === 'friend' && (from.flags & FrameFlag.alive) !== 0) {
        for (const e of units) {
          if (e.side !== 'enemy' || (e.flags & FrameFlag.alive) === 0) continue;
          if ((currentFrame()?.units[e.uid]?.[6] ?? -1) === uid) drawTargetArrow(ctx, scale, e, from, '#ff7a6a');
        }
      }
    }
    drawEffects(
      ctx,
      scale,
      S.result.events,
      S.playT,
      (uid) => {
        const u = byUid.get(uid);
        if (!u) return null;
        const downLabel = u.kind === 'tree' ? 'Baum fällt' : u.side === 'friend' ? (u.wounded ? 'Abschied' : 'verwundet') : 'vertrieben';
        // Wie viel XP ein entkommener Gegner mitnimmt (Fabi, Phase 3)
        const r = S.result!;
        const perEscape = r.escaped ? Math.round(r.xpLostToEscapes / r.escaped) : 0;
        const escapeLabel = u.side === 'friend' ? 'in Sicherheit' : `entkommen! −${perEscape} XP`;
        // Mit Figuren: Mitte des Körpers, Zahlen über dem Kopf
        const g = figureGeo(u, scale);
        if (g) return { x: u.x, y: g.cy / scale, r: (g.y - g.top) / scale / 2 + 0.5, downLabel, escapeLabel, side: u.side };
        return { x: u.x, y: u.y, r: u.radius, downLabel, escapeLabel, side: u.side };
      },
      (id) => getAbility(id).name,
      (uid, t) => {
        const f = S.result!.frames[Math.max(0, Math.floor(t / combatRules.tickSeconds) - 1)];
        const d = f?.units[uid];
        return d ? { x: d[0], y: d[1] } : null;
      },
      (id) => getAbility(id).visual === 'flight',
      { figures: figuresWanted() },
    );
    const frameAt = (uid: number, t: number) => {
      const f = S.result!.frames[Math.max(0, Math.floor(t / combatRules.tickSeconds) - 1)];
      const d = f?.units[uid];
      return d ? { x: d[0], y: d[1] } : null;
    };
    drawPoisonJumps(ctx, scale, S.result, units, S.playT, frameAt);
    drawFelledTrees(ctx, scale, S.result, S.playT, 'fall');
    if (biome) drawBiomeAir(ctx, scale, biome, visT(), !!S.run?.boss, rainAt(S.playT), STORM_TINT[S.storm] ?? 0);
    drawDuel(ctx, scale, S.result, S.playT);
    drawTelegraphs(ctx, scale, telegraphs, units, S.playT, true, 'top');
    drawRain(ctx, scale, S.storm, S.playT, rainAt(S.playT));
    drawCursor(ctx, scale, S.cursor, null);
  }
}

function renderHud(): void {
  const hud = $('hud');
  if (S.screen !== 'battle' || !S.result) {
    hud.textContent = '';
    return;
  }
  const rain = rainAt(S.playT);
  const t = Math.min(S.playT, S.result.duration);
  const parts = [`${fmt(t)} s`, `Sturm: ${stormName(S.storm)}`, `${String(S.speed).replace('.', ',')}×${S.paused ? ' · Pause' : ''}`];
  // K54: Kräftemessen statt Starkregen, wenn nur noch wenige stehen
  const duel = duelAt(S.result, S.playT);
  if (duel && S.playT < S.result.duration) parts.push(rain > 1 ? `Kräftemessen: Treffer ×${fmt(rain, 2)}` : 'Kräftemessen: Gleich werden die Treffer härter');
  else if (rain > 1) parts.push(`Starkregen: Treffer ×${fmt(rain, 2)}`);
  // K9: Was der Sturm gerade tut, in Worten
  const recent = S.result.events.filter((e) => e.t <= S.playT && e.t > S.playT - 1.2);
  if (recent.some((e) => e.type === 'gust')) parts.push('Böe: Der Wind schiebt eure Freunde');
  if (S.result.events.some((e) => e.type === 'lightningWarning' && e.t <= S.playT && e.strikeAt > S.playT)) parts.push('Blitz: im dunklen Kreis schlägt es gleich ein');
  if (S.result.events.some((e) => e.type === 'retreat' && e.t <= S.playT)) parts.push('Rückzug! Alle laufen zum Boot');
  if (S.playT >= S.result.duration) parts.push(S.result.outcome === 'win' ? 'Gewonnen!' : S.result.outcome === 'retreat' ? 'Rückzug!' : 'Niederlage');
  hud.textContent = parts.join(' · ');
}

// ---------- Eingabe ----------

let mouseUsed: () => void = () => {};

/** Eingaben auf dem Ufer, von Controller und Tastatur (X10) */
export function shoreAction(a: Action, source: 'key' | 'pad' = 'pad'): void {
  if (a === 'figures') return switchFigures();
  // Beim Aufstellen bleiben 1 und 2 auf der Tastatur der Sturm (nur in der Kampf-Übung)
  if (S.screen === 'setup' && (a === 'speed1' || a === 'speed2')) a = a === 'speed1' ? 'stormDown' : 'stormUp';
  if (S.mode === 'run' && S.screen === 'setup' && ['prev', 'next', 'stormDown', 'stormUp', 'reroll'].includes(a)) return;
  // Fabi (Phase Kern): Die Leertaste startet den Kampf und hält ihn an; zum Aufstellen mit der Tastatur bleibt sie „bestätigen“,
  // solange der Tastatur-Zeiger zu sehen ist
  if (source === 'key' && a === 'confirm') {
    if (S.screen === 'battle' && !S.endOverlay) return togglePause();
    if (S.screen === 'setup' && !S.cursor.visible && !S.held) return startBattle();
  }
  onAction(a);
}

function onAction(a: Action): void {
  S.cursor.visible = true;
  if (S.screen === 'setup') {
    switch (a) {
      case 'confirm': {
        if (S.held) return drop(S.cursor, false);
        const id = friendAt(S.cursor);
        if (id) return pickUp(id, 'field', S.cursor, false), refreshSetup();
        const hitEnemy = unitAt(view.setupUnits, S.cursor);
        const ei = hitEnemy !== null ? view.setupInfo[hitEnemy]?.enemyIndex : undefined;
        if (ei !== undefined) {
          S.setupSelected = S.setupSelected === ei ? null : ei;
          return;
        }
        const b = bench();
        if (b.length && S.placed.size < maxOnField() && insideFriendZone(S.cursor)) {
          S.placed.set(b[0]!, clampToZone(S.cursor));
          return refreshSetup();
        }
        if (b.length && S.placed.size >= maxOnField()) say(`Alle ${S.fieldSlots} Feldplätze sind belegt.`);
        return;
      }
      case 'back': {
        if (S.held) return drop(S.cursor, true);
        const id = friendAt(S.cursor);
        if (id) {
          S.placed.delete(id);
          S.marches.delete(id);
          refreshSetup();
        }
        return;
      }
      case 'start':
        return startBattle();
      case 'prev':
        return setLineup(S.lineupIdx - 1);
      case 'next':
        return setLineup(S.lineupIdx + 1);
      case 'stormDown':
        return setStorm(-1);
      case 'stormUp':
        return setStorm(1);
      case 'reroll':
        return newAttempt();
      case 'retreat':
        // K34 (Phase Kern): Ohne Wahl des Rückzugs wechselt T den Schützling der Leibwache
        return systemOn('retreatChoice') ? cycleRetreat() : cycleWard();
      case 'slots':
        return allAboard();
    }
  } else if (S.screen === 'battle') {
    if (S.endOverlay) {
      // Zwischenbildschirm: A weiter zur Übersicht, B Ufer betrachten
      if (a === 'confirm' || a === 'start') return show('summary');
      if (a === 'back') return startReview();
      return;
    }
    switch (a) {
      case 'prev':
        S.speed = SPEEDS[Math.max(0, SPEEDS.indexOf(S.speed) - 1)]!;
        return renderControls();
      case 'next':
        S.speed = SPEEDS[Math.min(SPEEDS.length - 1, SPEEDS.indexOf(S.speed) + 1)]!;
        return renderControls();
      case 'stormDown':
        return scrubTo(S.playT - 1);
      case 'stormUp':
        return scrubTo(S.playT + 1);
      case 'speed1':
      case 'speed2':
      case 'speed3':
      case 'speed4':
        S.speed = SPEEDS[Number(a.slice(-1)) - 1]!;
        return renderControls();
      case 'confirm': {
        // Zeiger über einer Figur: auswählen; sonst Pause
        const hit = S.cursor.visible ? unitAt(battleUnits(), S.cursor) : null;
        if (hit !== null) {
          S.selectedUid = hit === S.selectedUid ? null : hit;
          return renderSidebar();
        }
        return togglePause();
      }
      case 'back':
      case 'start':
        // Erst wenn der Kampf zu Ende gesehen ist (Fabi, Phase 3)
        if (!S.battleDone) return;
        if (S.result) S.playT = S.result.duration;
        return show('summary');
      default:
        return;
    }
  } else if (S.screen === 'summary') {
    if (a === 'confirm' || a === 'start') {
      leaveSummary();
    } else if (a === 'back') {
      replay();
    } else if (a === 'prev' || a === 'next') {
      cycleSummaryLog(a === 'next' ? 1 : -1);
    }
  }
}

let summaryLogUid: number | null = null;
function showSummaryLog(uid: number | null): void {
  summaryLogUid = uid;
  for (const row of document.querySelectorAll<HTMLElement>('#summary tr[data-uid]')) row.classList.toggle('selected', Number(row.dataset.uid) === uid);
  const box = document.getElementById('summary-log');
  if (!box) return;
  box.innerHTML = uid === null ? '' : `<h3>${displayName(uid)}</h3>${logHtml(uid)}`;
  const log = box.querySelector('.log');
  if (log) log.scrollTop = log.scrollHeight;
  if (uid !== null) box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

/** Controller: LB/RB blättert in der Übersicht durch die Figuren */
function cycleSummaryLog(delta: number): void {
  const uids = [...document.querySelectorAll<HTMLElement>('#summary tr[data-uid]')].map((r) => Number(r.dataset.uid));
  if (!uids.length) return;
  const i = summaryLogUid === null ? (delta > 0 ? 0 : uids.length - 1) : (uids.indexOf(summaryLogUid) + delta + uids.length) % uids.length;
  showSummaryLog(uids[i]!);
}

function setEndOverlay(on: boolean): void {
  S.endOverlay = on;
  document.body.classList.toggle('ended', on);
  if (on && S.result) {
    const r = S.result;
    $('end-title').textContent = r.outcome === 'win' ? 'Das Ufer ist frei' : r.outcome === 'retreat' ? 'Rückzug ans Boot' : 'Niederlage';
    ($('end-next') as HTMLButtonElement).focus({ preventScroll: true });
  }
}

/** Ufer betrachten: Zeitleiste frei, alles anklickbar, auch wer schon weg ist (Fabi, Phase 3) */
function startReview(): void {
  setEndOverlay(false);
  S.reviewing = true;
  S.paused = true;
  $('pause').textContent = 'Weiter';
  forgetSidebar();
  renderSidebar();
}

/** Vor- und Zurückspulen: Der Kampf ist aufgezeichnet, jede Stelle lässt sich anzeigen */
function scrubTo(t: number): void {
  if (!S.result) return;
  // Zurück ja, in die Zukunft nein, solange der Kampf nicht einmal zu Ende gesehen wurde
  const limit = S.battleDone ? S.result.duration : S.maxSeenT;
  S.playT = Math.max(0, Math.min(limit, t));
  S.paused = true;
  $('pause').textContent = 'Weiter';
  if (S.battleDone) S.reviewing = true;
  forgetSidebar();
  renderSidebar();
  syncTimeline();
}

function setBattleDone(done: boolean): void {
  S.battleDone = done;
  document.body.classList.toggle('battle-done', done);
}

let scrubbing = false;
function syncTimeline(): void {
  if (!S.result || scrubbing) return;
  const tl = $<HTMLInputElement>('timeline');
  // Spieltest Phase 4 (Fabi): Vorspulen geht nur bis dahin, wo man schon war, solange der Kampf noch läuft.
  // Fabi (Phase Kern, 17.10.2026 nachts): Solange der Kampf läuft, ist die Gegenwart immer das Ende der Leiste; man sieht
  // nicht, wie lange er noch dauert. Erst nach dem Ende umfasst die Leiste den ganzen Kampf
  tl.max = String(S.battleDone ? S.result.duration : Math.max(0.05, S.maxSeenT));
  tl.value = String(S.playT);
  tl.style.setProperty('--seen', '100%');
}

/** Phase Kern (Hinweise des Projekt-Leads in docs/kern.md): eine Taste schaltet zwischen Figuren und Kreisen um */
function switchFigures(): void {
  const on = toggleFigures();
  syncFiguresButtons();
  say(on ? 'Figuren: so sieht der Kampf mit den Bildern des Grafik-Leads aus.' : 'Kreise: so sah der Kampf bisher aus.');
  if (S.screen !== 'summary') draw();
}

function syncFiguresButtons(): void {
  for (const b of document.querySelectorAll<HTMLButtonElement>('.figures-toggle')) {
    b.textContent = figuresWanted() ? 'Kreise zeigen (G)' : 'Figuren zeigen (G)';
  }
}

function togglePause(): void {
  S.paused = !S.paused;
  $('pause').textContent = S.paused ? 'Weiter' : 'Pause';
}

// Maus: ziehen auf dem Feld und von der Bank
canvas.addEventListener('pointerdown', (ev) => {
  mouseUsed();
  S.cursor.visible = false;
  if (S.screen === 'battle' && S.result) {
    // L7: Figur anklicken zeigt ihren Verlauf; ins Leere klicken hebt die Auswahl auf
    S.selectedUid = unitAt(battleUnits(), pointerToField(ev));
    forgetSidebar();
    return renderSidebar();
  }
  if (S.screen !== 'setup') return;
  const p = pointerToField(ev);
  // S10: Am Schild-Griff ziehen und auf einem Freund loslassen setzt den Schützling
  const guard = wardKnobAt(p);
  if (guard) {
    S.wardAiming = guard;
    S.wardAim = p;
    canvas.setPointerCapture(ev.pointerId);
    return;
  }
  // K27: Am Griff ziehen setzt die Marschrichtung
  const aim = knobAt(p);
  if (aim) {
    S.aiming = aim;
    canvas.setPointerCapture(ev.pointerId);
    return;
  }
  const id = friendAt(p);
  const hit = unitAt(view.setupUnits, p);
  const ei = hit !== null ? view.setupInfo[hit]?.enemyIndex : undefined;
  S.setupSelected = ei !== undefined ? (S.setupSelected === ei ? null : ei) : null;
  if (id) {
    pickUp(id, 'field', p, true);
    canvas.setPointerCapture(ev.pointerId);
    refreshSetup();
  }
});
$('bench-list').addEventListener('pointerdown', (ev) => {
  const chip = (ev.target as HTMLElement).closest<HTMLElement>('[data-key]');
  if (!chip || S.screen !== 'setup') return;
  mouseUsed();
  ev.preventDefault();
  pickUp(chip.dataset.key!, 'bench', pointerToField(ev), true);
});
window.addEventListener('pointermove', (ev) => {
  const p = pointerToField(ev);
  if (S.wardAiming) {
    S.wardAim = p;
    return;
  }
  if (S.aiming) {
    aimAt(S.aiming, p);
    return;
  }
  if (S.held?.byMouse) {
    S.held.x = p.x;
    S.held.y = p.y;
    buildSetupUnits();
  }
  if (ev.target === canvas) {
    mouseUsed();
    S.cursor.visible = false;
    const units = S.screen === 'setup' ? view.setupUnits : S.result ? battleUnits() : [];
    S.hoverUid = unitAt(units, p);
    renderSidebar();
  }
  // K38 (Fabi, Stil-Werkbank): Fährt man über das Zeichen einer Eigenschaft, steht da, was sie tut
  traitTip(ev);
});
window.addEventListener('pointerup', (ev) => {
  if (S.wardAiming) {
    const key = S.wardAiming;
    S.wardAiming = null;
    S.wardAim = null;
    const target = friendAt(pointerToField(ev));
    if (target && target !== key) setWard(key, target);
    return;
  }
  if (S.aiming) return endAim();
  if (!S.held?.byMouse) return;
  // Das Feld hält den Zeiger beim Ziehen fest, darum zählt die Position, nicht das Ziel-Element
  const r = $('bench').getBoundingClientRect();
  const overBench = ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
  drop(pointerToField(ev), overBench);
});

function traitTip(ev: PointerEvent): void {
  const rect = canvas.getBoundingClientRect();
  if (ev.target !== canvas || !rect.width) return tipAt(null);
  const k = canvas.width / rect.width;
  const dx = (ev.clientX - rect.left) * k;
  const dy = (ev.clientY - rect.top) * k;
  const spot = traitSpots.find((t) => dx >= t.x - 2 && dx <= t.x + t.w + 2 && dy >= t.y - 2 && dy <= t.y + t.h + 2);
  if (!spot) return tipAt(null);
  tipAt(spot.text, { left: rect.left + spot.x / k, top: rect.top + spot.y / k, right: rect.left + (spot.x + spot.w) / k, bottom: rect.top + (spot.y + spot.h) / k });
}

// Kopfleiste
$<HTMLSelectElement>('lineup').addEventListener('change', (ev) => setLineup(Number((ev.target as HTMLSelectElement).value)));
$<HTMLSelectElement>('storm').addEventListener('change', (ev) => {
  S.storm = (ev.target as HTMLSelectElement).value;
  refreshSetup();
});
$<HTMLSelectElement>('retreat').addEventListener('change', (ev) => {
  S.retreat = (ev.target as HTMLSelectElement).value;
  refreshSetup();
});
$('reroll').addEventListener('click', () => newAttempt());
$('start').addEventListener('click', () => startBattle());
$('skip').addEventListener('click', () => {
  if (S.battleDone) onAction('back');
});
$('pause').addEventListener('click', () => togglePause());
$('end-next').addEventListener('click', () => show('summary'));
$('end-review').addEventListener('click', () => startReview());
$('all-aboard').addEventListener('click', () => allAboard());
for (const b of document.querySelectorAll<HTMLButtonElement>('.figures-toggle')) b.addEventListener('click', () => switchFigures());
syncFiguresButtons();
const timeline = $<HTMLInputElement>('timeline');
timeline.addEventListener('input', () => {
  scrubbing = true;
  scrubTo(Number(timeline.value));
  // Hinter das Gesehene geht es nicht: der Regler springt zurück an die Grenze
  if (Number(timeline.value) > S.playT + 0.01) timeline.value = String(S.playT);
});
for (const ev of ['change', 'pointerup', 'blur'] as const) {
  timeline.addEventListener(ev, () => {
    scrubbing = false;
    syncTimeline();
  });
}
window.addEventListener('pointerup', () => {
  if (scrubbing) {
    scrubbing = false;
    syncTimeline();
  }
});
for (const b of document.querySelectorAll<HTMLButtonElement>('[data-speed]')) {
  b.addEventListener('click', () => {
    S.speed = Number(b.dataset.speed);
    renderControls();
  });
}

// ---------- Hauptschleife und Einstieg ----------

/** Ein Bild auf dem Ufer; Eingabe und Taktgeber kommen von der App */
/** Nach dem Umschalten im Menü (Figuren oder Kreise) neu zeichnen */
export function shoreRedraw(): void {
  syncFiguresButtons();
  if (S.screen !== 'summary') draw();
}

export function shoreFrame(dt: number, st: InputState): void {
  if (st.move.x !== 0 || st.move.y !== 0) {
    S.cursor.visible = true;
    S.cursor.x = Math.min(FIELD.width - 2, Math.max(2, S.cursor.x + st.move.x * 70 * dt));
    S.cursor.y = Math.min(FIELD.height - 2, Math.max(2, S.cursor.y + st.move.y * 70 * dt));
    if (S.held && !S.held.byMouse) {
      S.held.x = S.cursor.x;
      S.held.y = S.cursor.y;
      buildSetupUnits();
    }
    const units = S.screen === 'setup' ? view.setupUnits : S.result ? battleUnits() : [];
    S.hoverUid = unitAt(units, S.cursor);
    renderSidebar();
  }
  if (S.screen === 'battle' && S.result) {
    const before = S.playT;
    if (!S.paused && S.playT < S.result.duration) S.playT = Math.min(S.result.duration, S.playT + dt * S.speed * BASE_SPEED);
    // Runde 5: Treffer, Kniffe, wer fällt und wer vertrieben ist, sind zu hören
    battleSounds(S.result, before, S.playT);
    // nach dem Ende laufen die Bewegungen noch ein paar Sekunden weiter (Verpuffen, Abschied, Stehen)
    if (S.playT >= S.result.duration - 1e-6) S.afterT = Math.min(30, S.afterT + dt * S.speed * BASE_SPEED);
    else S.afterT = 0;
    S.maxSeenT = Math.max(S.maxSeenT, S.playT);
    if (S.playT >= S.result.duration && !S.battleDone) {
      battleEndSound(S.result.outcome);
      setBattleDone(true);
      setEndOverlay(true);
    }
    syncTimeline();
    if (S.hoverUid !== null || S.selectedUid !== null) renderSidebar();
  }
  renderHud();
  if (S.screen !== 'summary') draw();
}

export function initShore(opts: { mouseUsed: () => void }): void {
  mouseUsed = opts.mouseUsed;
  // Phase Kern: die Figuren des Grafik-Leads; bis sie geladen sind, bleiben die Kreise
  loadArt();
}

/** Für Browser-Tests: wo die Freunde stehen, wohin sie marschieren und wo die Zeichen der Eigenschaften sitzen (Gerätepixel) */
export function shoreDebug(): { placed: [string, { x: number; y: number }][]; marches: [string, { dx: number; dy: number }][]; speed: number; traitSpots: { x: number; y: number; w: number; h: number; text: string }[]; events: unknown[]; seek: (t: number) => void } {
  // seek (Erste Fassung): zum Prüfen im Browser an eine Stelle des Kampfs springen und anhalten
  const seek = (t: number) => {
    S.playT = t;
    S.paused = true;
  };
  return { placed: [...S.placed], marches: [...S.marches], speed: S.speed, traitSpots: traitSpots.map((t) => ({ ...t })), events: S.result?.events ?? [], seek };
}

function resetShore(): void {
  S.placed.clear();
  S.marches.clear();
  S.wards.clear();
  S.wardAiming = null;
  S.wardAim = null;
  S.aiming = null;
  S.held = null;
  S.cursor.holding = null;
  S.result = null;
  S.setupSelected = null;
  S.selectedUid = null;
  S.hoverUid = null;
  S.message = '';
  forgetSidebar();
}

/** Kampf-Übung: der Kampf-Prototyp aus Phase 3 */
export function openPractice(): void {
  resetShore();
  S.mode = 'practice';
  S.run = null;
  S.roster = PRACTICE;
  S.fieldSlots = FIELD_SLOTS;
  S.attempt = 0;
  rerollEnemies();
  document.body.dataset.mode = 'practice';
  show('setup');
}

/** Kampf im Run (R27): Der Landtrupp steht schon fest, die Gegner auch; aufstellen und los */
export function openRunFight(ctx: RunShoreContext): void {
  resetShore();
  S.mode = 'run';
  S.run = ctx;
  S.roster = ctx.roster;
  S.fieldSlots = ctx.fieldSlots;
  S.enemies = ctx.enemies;
  S.storm = ctx.storm;
  S.retreat = ctx.noRetreat ? 'letzter' : ctx.retreat;
  for (const p of ctx.suggest(ctx.roster.map((m) => m.key))) S.placed.set(p.id, { x: p.x, y: p.y });
  document.body.dataset.mode = 'run';
  show('setup');
}

/**
 * Neuladen mitten im Kampf (Fabi, Spieltest Phase 4): Das Ergebnis steht schon im Run, der Kampf läuft
 * noch einmal von vorn ab, genau so wie vorher (gleicher Startwert, deterministisch).
 */
export function replayRunFight(ctx: RunShoreContext, result: BattleResult): void {
  resetShore();
  S.mode = 'run';
  S.run = ctx;
  S.roster = ctx.roster;
  S.fieldSlots = ctx.fieldSlots;
  S.enemies = ctx.enemies;
  S.storm = ctx.storm;
  S.retreat = ctx.retreat;
  document.body.dataset.mode = 'run';
  beginPlayback(result);
}
